/**
 * Générateur — O10 : utiliser une formule liant deux grandeurs dans une
 * situation de proportionnalité.
 * Attendu ID : 173 | Niveau : 4ème | Domaine : proportionnalite
 *
 * Formules de l'exemple de réussite : la longueur parcourue à vitesse
 * constante (d = v × t, et ses deux sens inverses du cours), la longueur d'un
 * cercle en fonction du diamètre (L = π × d), la loi d'Ohm (U = R × I, formule
 * toujours écrite dans l'énoncé). Toutes les valeurs sont exactes par
 * construction (rationnels de proportionnalite.js), recalculées par un second
 * chemin dans le gen (throw sinon). Aucun solveur test-side ne couvre 173 ; le
 * test jetable tmp-revue-173 relit les grandeurs dans le texte affiché.
 *
 * Revue 2026-09-23 (vague 3), décisions du coordinateur appliquées en mode
 * auto. Attendu de fin de 4e (Eduscol, p. 6) : « Il utilise une formule liant
 * deux grandeurs dans une situation de proportionnalité. » Exemple de
 * réussite : « Il utilise des formules telles que la loi d'Ohm, la longueur
 * d'un cercle en fonction du diamètre, la longueur parcourue à vitesse
 * constante en fonction du temps […] pour calculer des grandeurs. » Cours
 * (19Proport2) : « vitesse moyenne v = d/t », « Conséquence : d = v × t »,
 * 420 km en 1 h 45 min, tableau durée en minutes et distance ; (19Espace1)
 * longueur du cercle 2 × π × 5 = 10π, valeur exacte puis arrondi.
 *   - Rappel brouillon : AUCUN (décision : un produit, et π à la calculatrice).
 *   - formule_texte RECADRÉ : AJOUT des deux sens inverses du cours (vitesse
 *     moyenne v = d ÷ t, durée t = d ÷ v) et des durées en minutes (15 min,
 *     30 min, 1 h 30 min… ; erreur visée minutes_pour_heures, Eduscol 4e 2024
 *     q. 11), plus jamais 1 h (la réponse serait la vitesse) ; vitesses
 *     plausibles par mobile (randonneur 4 à 6 km/h, cycliste 12 à 24, voiture
 *     50 à 130, train 160 à 300 ; jamais 70 km/h, réservé au coup de pouce du
 *     cadrage) ; la durée demandée se donne en minutes, ou en heures quand elle
 *     tombe juste (la consigne le dit). Loi d'Ohm gardée, formule ÉCRITE dans
 *     l'énoncé (mode auto, À ARBITRER : hors du cours de maths), valeurs
 *     bornées à 24 V (petits entiers, À ARBITRER aussi).
 *   - cercle_texte RECADRÉ, libellé « Longueur d'un cercle » (le mot du cours,
 *     plus « périmètre ») : deux régimes. Valeur EXACTE : le rayon est donné,
 *     l'élève écrit le nombre devant « π cm » (suffixe du champ, rien à taper
 *     en π) ; le diamètre n'est jamais donné dans ce régime, sa réponse serait
 *     écrite dans l'énoncé ; l'élève qui recopie le π (« 6π ») est accepté
 *     (comparaison d'expression, liste finie des écritures admises). La
 *     décision proposait un trou « {{8}}π cm » : même affichage, mais une
 *     saisie à suffixe garde un seul type de payload sous la clé
 *     cercle_texte. Valeur APPROCHÉE : le diamètre est donné, la
 *     consigne dit « touche π de ta calculatrice » et « arrondi au dixième »,
 *     `contenu.calculatrice = true`. « π ≈ 3,14 » disparaît (mode auto, À
 *     ARBITRER) ; les diamètres sont choisis pour que 3,14 × d et π × d aient
 *     le même arrondi au dixième, loin de la moitié : un élève qui prend 3,14
 *     n'est pas compté faux. Contextes plausibles (plus de rond-point de
 *     6 cm) ; figure svgCercle (cercle, centre O, rayon ou diamètre coté),
 *     `alt`.
 *   - formule_qcm_unique RECADRÉ : la question ne recopie plus la formule ;
 *     « Quel calcul donne la distance parcourue, en km, en 30 min ? » à
 *     vitesse constante, distracteurs nommés minutes_pour_heures (la durée
 *     lue 0,30 h ; ou le nombre de minutes pris pour des heures), inversion,
 *     additif ; calculs en KaTeX ; contrat exporté. Ajout du correcteur :
 *     inversion et additif portent sur le nombre de minutes écrit (80 : 30,
 *     80 + 30) ; sur la durée convertie (80 : 0,5), ils répétaient l'opérande
 *     de la clé, que désignait sans calcul l'option « × » au nombre le plus
 *     répété (100 % des tirages).
 *   - tableau_texte_trou RECADRÉ, libellé « Tableau des distances » : d = v × t
 *     comme au tableau du cours (durées en minutes, distances en km) au lieu de
 *     U = R × I ; jamais 60 min (la case vaudrait la vitesse écrite) ni une
 *     vitesse de 60 km/h ; aucun trou dont la réponse est écrite ailleurs ;
 *     étiquettes-pièges minutes_pour_heures et recopie ; au moins deux
 *     affectations gardent la ligne croissante.
 *   - formule_tableau_vf RECADRÉ : quatre lignes (avant cinq, trois vraies
 *     toujours), une par type (distance, durée, formule, propriété), chacune
 *     tirée vraie ou fausse : une à trois vraies, la fausse change de type.
 *     Ajout du correcteur : la ligne de durée affiche 25 min, 50 min,
 *     1 h 25 min, 1 h 50 min ou 2 h 50 min, qu'elle soit vraie ou fausse (la
 *     lecture fautive 1,25 h → « 1 h 25 min » se reconnaissait à ses minutes
 *     dans 100 % des tirages) ; vitesses multiples de 3 sur cette carte.
 *   - formule_relier GARDÉ : lettres et formule en KaTeX, « Deux tensions sont
 *     en trop. », intensités de 1 à 4 A (tensions de 24 V au plus).
 *   - Pas de carte « arc de cercle » (cours : « Pour expert » ; absente du BO
 *     2025).
 * Six formats (avant : six).
 */

import {
    randomChoice, randInt, shuffle, filtrerDistracteurs,
    makeSaisie, makeQcm, makeTexteTrou, makeVraiFaux, makeRelier, tex,
} from '../../../utils';
import {
    ratMul, ratDiv, ratAdd, ratEq, ratN, rat, ratFromStr, ratToDecFr, ratVal, nbDecimales,
    formatFr, decTex, tableEnonce, ligneTable, imgElement, svgCercle,
} from './proportionnalite';

const ATTENDU_ID = 173;
const L_FORMULE = 'Utiliser une formule de proportionnalité';
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const nb = (r) => formatFr(ratToDecFr(r));
const T = (s) => `\\text{${s}}`;

// ═══════════════════════════════════════════════════════════
// Durées (en quarts d'heure) et mobiles
// ═══════════════════════════════════════════════════════════

/** q quarts d'heure → « 15 min », « 1 h 30 min », « 2 h ». */
const dureeTexte = (q) => {
    const h = Math.floor(q / 4); const m = (q % 4) * 15;
    if (h === 0) return `${m} min`;
    return m === 0 ? `${h} h` : `${h} h ${m} min`;
};
/** Durée en heures, rationnel exact. */
const heures = (q) => rat(q, 4);
/** L'erreur minutes_pour_heures : 1 h 30 min lue 1,30 h (h + m/100). */
const heuresMalLues = (q) => ratAdd(ratN(Math.floor(q / 4)), rat((q % 4) * 15, 100));
// Jamais 1 h (q = 4) : la distance serait la vitesse, écrite dans l'énoncé.
const Q_NON_ENTIERS = [1, 2, 3, 5, 6, 7, 10];
const Q_TOUS = [1, 2, 3, 5, 6, 7, 8, 10, 12];

// Vitesses plausibles par mobile ; 70 km/h est réservé au coup de pouce du cadrage.
const MOBILES = [
    { id: 'voiture', sujet: 'Une voiture roule', le: 'la voiture', vs: [50, 60, 80, 90, 100, 110, 120, 130], qMax: 12 },
    { id: 'cycliste', sujet: 'Un cycliste roule', le: 'le cycliste', vs: [12, 15, 16, 18, 20, 24], qMax: 12 },
    { id: 'train', sujet: 'Un train roule', le: 'le train', vs: [160, 180, 200, 240, 300], qMax: 8 },
    { id: 'randonneur', sujet: 'Un randonneur marche', le: 'le randonneur', vs: [4, 5, 6], qMax: 12 },
];

// ═══════════════════════════════════════════════════════════
// 1. SAISIE — appliquer une formule, dans les deux sens
// ═══════════════════════════════════════════════════════════

const vitesseDistance = () => {
    const mob = randomChoice(MOBILES);
    const v = randomChoice(mob.vs);
    const q = randomChoice(Q_NON_ENTIERS.filter((x) => x <= mob.qMax));
    const d = ratMul(ratN(v), heures(q));
    if (!ratEq(ratDiv(d, heures(q)), ratN(v))) throw new Error('gen_173 : distance incohérente');
    return {
        reponse: nb(d), suffixe: 'km', famille: 'distance',
        texte: `<p>${mob.sujet} à la vitesse constante de ${v} km/h.</p>`
            + `<p><b>Quelle est la distance parcourue en ${dureeTexte(q)} ?</b></p>`,
        params: { v, duree: dureeTexte(q), erreurs_visees: { minutes_pour_heures: nb(ratMul(ratN(v), heuresMalLues(q))) } },
    };
};

const vitesseMoyenne = () => {
    const mob = randomChoice(MOBILES);
    const v = randomChoice(mob.vs);
    const q = randomChoice(Q_NON_ENTIERS.filter((x) => x <= mob.qMax));
    const d = ratMul(ratN(v), heures(q));
    if (!ratEq(ratDiv(d, heures(q)), ratN(v))) throw new Error('gen_173 : vitesse incohérente');
    return {
        reponse: String(v), suffixe: 'km/h', famille: 'vitesse',
        texte: `<p>${mob.sujet.replace(/ (roule|marche)$/, '')} parcourt ${nb(d)} km en ${dureeTexte(q)}, à vitesse constante.</p>`
            + '<p><b>Quelle est sa vitesse, en km/h ?</b></p>',
        params: { d: nb(d), duree: dureeTexte(q), erreurs_visees: { minutes_pour_heures: 'd divisé par la durée mal lue' } },
    };
};

const dureeTrajet = () => {
    const mob = randomChoice(MOBILES);
    const v = randomChoice(mob.vs);
    const q = randomChoice(Q_TOUS.filter((x) => x <= mob.qMax));
    const d = ratMul(ratN(v), heures(q));
    const enHeures = q % 4 === 0;
    const reponse = enHeures ? String(q / 4) : String(15 * q);
    if (!ratEq(ratMul(ratDiv(d, ratN(v)), ratN(enHeures ? 1 : 60)), ratN(Number(reponse)))) throw new Error('gen_173 : durée incohérente');
    return {
        reponse, suffixe: enHeures ? 'h' : 'min', famille: 'duree',
        texte: `<p>${mob.sujet} à la vitesse constante de ${v} km/h.</p>`
            + `<p><b>Quelle durée faut-il pour parcourir ${nb(d)} km ? Donne-la en ${enHeures ? 'heures' : 'minutes'}.</b></p>`,
        params: { v, d: nb(d) },
    };
};

// Loi d'Ohm : formule écrite (hors du cours de maths), tensions de 24 V au plus.
const loiOhm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const R = randomChoice([2, 3, 4, 5, 6, 8, 10, 12]);
        const I = ratFromStr(randomChoice(['0,5', '1', '1,5', '2', '2,5', '3']));
        const U = ratMul(ratN(R), I);
        if (ratVal(U) > 24) continue;
        return {
            reponse: nb(U), suffixe: 'V', famille: 'ohm',
            texte: `<p>La loi d'Ohm s'écrit ${tex('U = R \\times I')}, avec la tension ${tex('U')} en volts, la résistance ${tex('R')} en ohms et l'intensité ${tex('I')} en ampères.</p>`
                + `<p>Une résistance de ${R} Ω est traversée par un courant d'intensité ${nb(I)} A.</p>`
                + `<p><b>Quelle est la tension ${tex('U')} à ses bornes ?</b></p>`,
            params: { R, I: nb(I) },
        };
    }
    throw new Error('gen_173 : loi d\'Ohm, aucun tirage');
};

const genFormuleSaisie = () => {
    const t = randInt(0, 19);
    const f = t < 6 ? vitesseDistance() : t < 11 ? vitesseMoyenne() : t < 15 ? dureeTrajet() : loiOhm();
    const payload = makeSaisie(f.reponse, f.texte, { ...opts(L_FORMULE), strict: false, suffixe: f.suffixe });
    payload._meta.params = { famille: f.famille, ...f.params };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// 2. LONGUEUR D'UN CERCLE — valeur exacte, ou arrondi à la calculatrice
// ═══════════════════════════════════════════════════════════

// Valeur exacte : le RAYON est donné (réponse 2r devant « π »).
const CERCLES_RAYON = [
    { phrase: (r) => `Un cercle a un rayon de ${r} cm.`, question: 'Quelle est la longueur de ce cercle ?', unite: 'cm', rs: [3, 4, 5, 6, 7, 8, 9, 12] },
    { phrase: (r) => `Une roue de vélo a un rayon de ${r} cm.`, question: 'Quelle est la longueur du cercle formé par le bord de la roue ?', unite: 'cm', rs: [30, 32, 33, 35] },
    { phrase: (r) => `Un bassin circulaire a un rayon de ${r} m.`, question: 'Quelle est la longueur du bord du bassin ?', unite: 'm', rs: [2, 3, 4, 5, 6] },
    { phrase: (r) => `Un rond-point circulaire a un rayon de ${r} m.`, question: 'Quelle est la longueur du bord du rond-point ?', unite: 'm', rs: [10, 12, 15, 18, 20] },
];
// Valeur approchée : le DIAMÈTRE est donné. Diamètres où π × d et 3,14 × d ont le
// même arrondi au dixième, à plus d'un centième de la moitié : l'élève qui prend
// 3,14 n'est pas compté faux, et aucun arrondi n'est fragile. Jamais un entier.
const CERCLES_DIAMETRE = [
    { phrase: (d) => `Un cercle a un diamètre de ${d} cm.`, question: 'Quelle est la longueur de ce cercle ?', unite: 'cm', ds: [3, 4, 5, 8, 9, 10, 12] },
    { phrase: (d) => `Une assiette a un diamètre de ${d} cm.`, question: 'Quelle est la longueur du bord de l\'assiette ?', unite: 'cm', ds: [20, 22, 24, 25, 27] },
    { phrase: (d) => `Le cadran d'une horloge a un diamètre de ${d} cm.`, question: 'Quelle est la longueur du bord du cadran ?', unite: 'cm', ds: [20, 22, 24, 25, 27, 29, 32] },
    { phrase: (d) => `Un bassin circulaire a un diamètre de ${d} m.`, question: 'Quelle est la longueur du bord du bassin ?', unite: 'm', ds: [3, 4, 5, 8, 9, 10, 12] },
    { phrase: (d) => `Un rond-point circulaire a un diamètre de ${d} m.`, question: 'Quelle est la longueur du bord du rond-point ?', unite: 'm', ds: [20, 22, 24, 25, 27, 29, 32, 34, 37, 39, 44, 49] },
];

/** Arrondi au dixième de π × d, vérifié : même arrondi avec 3,14, loin de la moitié, jamais entier. */
const arrondiDixieme = (d) => {
    const dixiemes = Math.PI * d * 10;
    const a = Math.round(dixiemes);
    const b = Math.round(314 * d / 10);          // 3,14 × d, en dixièmes (exact : 314·d/10)
    const reste = dixiemes - Math.floor(dixiemes);
    if (a !== b || Math.abs(reste - 0.5) < 0.1 || a % 10 === 0) throw new Error(`gen_173 : diamètre ${d} fragile`);
    return `${Math.floor(a / 10)},${a % 10}`;
};

const genCercleSaisie = () => {
    if (randInt(0, 1) === 0) {
        const ctx = randomChoice(CERCLES_RAYON);
        const r = randomChoice(ctx.rs);
        const n = 2 * r;
        // « π cm » est affiché après le champ ; l'élève qui recopie le π (« 6π »)
        // a juste aussi : liste finie des écritures admises (comparaison d'expression).
        const admises = [`${n}π`, `${n}*π`, `π*${n}`, `${n}pi`, `${n}*pi`, `2*${r}*π`, `2*π*${r}`];
        const payload = makeSaisie(String(n),
            `<p>${ctx.phrase(r)}</p>`
            + `<p><b>${ctx.question} Donne la valeur exacte.</b></p>`,
            { ...opts(L_FORMULE), strict: false, suffixe: `π ${ctx.unite}`,
              comparaison: 'expression', reponses_acceptees: admises,
              elements_additionnels: imgElement(svgCercle(`${r} ${ctx.unite}`, true), 'small',
                  `Un cercle de centre O ; un rayon est tracé, il mesure ${r} ${ctx.unite}.`) });
        payload._meta.params = { regime: 'exacte', rayon: r, unite: ctx.unite };
        return payload;
    }
    const ctx = randomChoice(CERCLES_DIAMETRE);
    const d = randomChoice(ctx.ds);
    const payload = makeSaisie(arrondiDixieme(d),
        `<p>${ctx.phrase(d)}</p>`
        + `<p><b>${ctx.question} Utilise la touche π de ta calculatrice, puis donne l'arrondi au dixième.</b></p>`,
        { ...opts(L_FORMULE), strict: false, suffixe: ctx.unite,
          elements_additionnels: imgElement(svgCercle(`${d} ${ctx.unite}`, false), 'small',
              `Un cercle de centre O ; un diamètre est tracé, il mesure ${d} ${ctx.unite}.`) });
    // Calculatrice : la longueur approchée se calcule avec la touche π (pratique du
    // cours : valeur exacte, puis arrondi), l'arrondi au dixième en dépend.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.params = { regime: 'approchee', diametre: d, unite: ctx.unite };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// 3. QCM — quel calcul donne la distance ? (durée en minutes)
// ═══════════════════════════════════════════════════════════

export const REGLES_DISTRACTEURS_QCM = {
    formule_qcm_unique: {
        support: 'un mobile roule à v km/h (vitesse plausible pour ce mobile) pendant une durée écrite en minutes ou en heures et minutes, jamais une heure entière (15 min, 30 min, 45 min, 1 h 15 min, 1 h 30 min, 1 h 45 min, 2 h 30 min) ; clé v × t, la durée t en heures décimales ; la formule n\'est pas écrite dans la question',
        familles: {
            minutes_pour_heures: 'la durée mal convertie (Eduscol 4e 2024 q. 11) : v × 0,30 pour 30 min, v × 1,3 pour 1 h 30 min (toujours présente), ou v × 30, v × 90, le nombre de minutes pris pour des heures',
            inversion: 'v : m, la division à la place de la multiplication, sur le nombre de minutes écrit (80 : 30 pour 30 min)',
            additif: 'v + m, l\'addition des deux nombres de la question, la durée en minutes (80 + 30)',
        },
        supprimees: {
            formule_recopiee: 'l\'ancienne question « (d = v × t) » recopiait la formule : la clé en était le calque dans 100 % des tirages',
            perimetre_314: 'l\'ancienne famille du cercle avec π ≈ 3,14 (sortie avec 3,14)',
        },
        garantie: 'quatre choix : la clé, v × (h + m/100) et deux familles parmi v × m, v : m, v + m (m, le nombre de minutes) ; l\'opérande de la clé n\'apparaît qu\'une fois ; valeurs recalculées en rationnels, deux à deux distinctes (nouveau tirage borné sinon) ; écritures deux à deux distinctes',
    },
};

const genFormuleQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const mob = randomChoice(MOBILES);
        const v = randomChoice(mob.vs);
        const q = randomChoice(Q_NON_ENTIERS.filter((x) => x <= mob.qMax));
        const t = heures(q); const tMal = heuresMalLues(q); const minutes = 15 * q;
        const options = [
            { code: null, latex: `${v} \\times ${decTex(nb(t))}`, v: ratMul(ratN(v), t) },
            { code: 'minutes_pour_heures', latex: `${v} \\times ${decTex(nb(tMal))}`, v: ratMul(ratN(v), tMal) },
        ];
        // inversion et additif portent sur le nombre de minutes écrit dans la
        // question (80 : 30, 80 + 30). Portés sur la durée convertie (80 : 0,5,
        // 80 + 0,5), ils répétaient l'opérande de la clé : l'option « × » au
        // nombre le plus répété était la clé dans 100 % des tirages.
        const autres = shuffle([
            { code: 'minutes_pour_heures', latex: `${v} \\times ${minutes}`, v: ratN(v * minutes) },
            { code: 'inversion', latex: `${v} : ${minutes}`, v: rat(v, minutes) },
            { code: 'additif', latex: `${v} + ${minutes}`, v: ratN(v + minutes) },
        ]).slice(0, 2);
        options.push(...autres);
        if (new Set(options.map((o) => ratToDecFr(o.v) ?? `${o.v.p}/${o.v.q}`)).size !== 4) continue;
        if (new Set(options.map((o) => o.latex)).size !== 4) continue;
        // second chemin : la clé redonne v quand on la divise par la durée
        if (!ratEq(ratDiv(options[0].v, t), ratN(v))) throw new Error('gen_173 QCM : clé incohérente');
        const choix = options.map((o) => tex(o.latex));
        const payload = makeQcm(choix, choix[0],
            `<p>${mob.sujet} à la vitesse constante de ${v} km/h.</p>`
            + `<p><b>Quel calcul donne la distance parcourue, en km, en ${dureeTexte(q)} ?</b></p>`,
            opts(L_FORMULE));
        payload._meta.params = { mobile: mob.id, v, duree: dureeTexte(q), distracteurs: options.slice(1).map((o) => o.code) };
        return payload;
    }
    throw new Error('gen_173 QCM : aucun tirage valide');
};

// ═══════════════════════════════════════════════════════════
// 4. TABLEAU DES DISTANCES — d = v × t, durées en minutes (drag)
// ═══════════════════════════════════════════════════════════

const MINUTES = [10, 15, 20, 30, 40, 45, 50, 90, 120, 150];

const genTableauTrou = () => {
    for (let essai = 0; essai < 200; essai++) {
        const mob = randomChoice(MOBILES);
        const v = randomChoice(mob.vs.filter((x) => x !== 60));
        const possibles = MINUTES.filter((m) => m <= 15 * mob.qMax && nbDecimales(rat(v * m, 60)) <= 2);
        if (possibles.length < 4) continue;
        const ms = shuffle(possibles).slice(0, 4).sort((a, b) => a - b);
        const ds = ms.map((m) => rat(v * m, 60));
        const trous = shuffle([0, 1, 2, 3]).slice(0, 2).sort((a, b) => a - b);
        const connus = [0, 1, 2, 3].filter((i) => !trous.includes(i));
        const mots = trous.map((i) => nb(ds[i]));
        // aucun trou dont la réponse est écrite ailleurs (vitesse, durées, cases connues)
        const ecrits = [ratN(v), ...ms.map(ratN), ...connus.map((i) => ds[i])];
        if (trous.some((i) => ecrits.some((e) => ratEq(e, ds[i])))) continue;
        // étiquettes-pièges : minutes_pour_heures (m min lues m/100 h), recopie (m)
        const cand = [
            ...trous.map((i) => ({ v: rat(v * ms[i], 100), code: 'minutes_pour_heures' })),
            ...trous.map((i) => ({ v: ratN(ms[i]), code: 'recopie' })),
        ].filter((c) => nbDecimales(c.v) <= 2 && !connus.some((i) => ratEq(ds[i], c.v)));
        const phrase = trous.map((i, j) => `$${T('En ')}${ms[i]}${T(' min : ')}{{${mots[j]}}}${T(' km')}$`).join('\n');
        const textes = filtrerDistracteurs(phrase, cand.map((c) => nb(c.v)));
        const retenus = cand.filter((c, i, arr) => textes.includes(nb(c.v)) && arr.findIndex((d) => nb(d.v) === nb(c.v)) === i);
        const mph = retenus.filter((c) => c.code === 'minutes_pour_heures');
        const rec = retenus.filter((c) => c.code === 'recopie');
        if (!mph.length || !rec.length || retenus.length < 3) continue;
        const choisis = [mph[0], rec[0], ...shuffle([...mph.slice(1), ...rec.slice(1)]).slice(0, 1)];
        const distracteurs = choisis.map((c) => nb(c.v));
        // au moins deux affectations gardent la ligne croissante
        const etiquettes = [...mots, ...distracteurs];
        let croissantes = 0;
        etiquettes.forEach((e1) => etiquettes.forEach((e2) => {
            if (e1 === e2) return;
            const ligne = ds.map((d, i) => (i === trous[0] ? ratVal(ratFromStr(e1.replace(/\s/g, ''))) : (i === trous[1] ? ratVal(ratFromStr(e2.replace(/\s/g, ''))) : ratVal(d))));
            if (ligne.every((x, i) => i === 0 || x > ligne[i - 1])) croissantes++;
        }));
        if (croissantes < 2) continue;
        // second chemin : v = d ÷ t sur chaque colonne
        ds.forEach((d, i) => { if (!ratEq(ratDiv(d, rat(ms[i], 60)), ratN(v))) throw new Error('gen_173 tableau : incohérent'); });
        const tableau = tableEnonce(
            ligneTable('Durée (min)', ms.map(String)),
            ligneTable('Distance (km)', ds.map((d, i) => (trous.includes(i) ? '…' : nb(d)))),
        );
        const payload = makeTexteTrou(phrase,
            `<p>${mob.sujet} à la vitesse constante de ${v} km/h.</p>\n${tableau}`
            + '<p><b>Complète les deux cases manquantes du tableau des distances parcourues.</b></p>',
            { ...opts(L_FORMULE), mode: 'drag', distracteurs });
        payload._meta.params = { mobile: mob.id, v, minutes: ms, trous, distracteurs: choisis.map((c) => c.code) };
        return payload;
    }
    throw new Error('gen_173 tableau : aucun tirage valide');
};

// ═══════════════════════════════════════════════════════════
// 5. VRAI/FAUX — une situation à vitesse constante, quatre affirmations
// ═══════════════════════════════════════════════════════════

const FORMULES_VF = [
    { vraie: 'La distance se calcule avec $\\mathrm{}d = v \\times t$.', fausse: 'La distance se calcule avec $\\mathrm{}d = v : t$.' },
    { vraie: 'La vitesse se calcule avec $\\mathrm{}v = d : t$.', fausse: 'La vitesse se calcule avec $\\mathrm{}v = d \\times t$.' },
    { vraie: 'La durée se calcule avec $\\mathrm{}t = d : v$.', fausse: 'La durée se calcule avec $\\mathrm{}t = v : d$.' },
];
const PROPRIETES_VF = {
    vraies: [
        'Si la durée du trajet double, la distance parcourue double aussi.',
        'La distance parcourue est proportionnelle à la durée du trajet.',
    ],
    fausses: [
        'Si la durée du trajet augmente de 1 h, la distance parcourue augmente de 1 km.',
        'Si la durée du trajet double, la distance parcourue est multipliée par 4.',
    ],
};
// Durées dont la lecture décimale fautive s'écrit (0,5 h lue « 50 min »…).
const Q_LISIBLES = [1, 2, 5, 6, 10];
const dureeMalLue = (q) => {
    const h = Math.floor(q / 4); const centiemes = (q % 4) * 25;
    return h === 0 ? `${centiemes} min` : `${h} h ${centiemes} min`;
};
// Ligne « Il faut … » : la durée affichée est toujours 25 min, 50 min,
// 1 h 25 min, 1 h 50 min ou 2 h 50 min, que la ligne soit vraie ou fausse. La
// fausse est la lecture fautive d'une durée en quarts d'heure (1,25 h lue
// « 1 h 25 min ») ; la vraie, une durée de même écriture calculée juste (50 min
// pour 10 km à 12 km/h). Avant, les minutes disaient le verdict : 25 ou 50 min
// pour la fausse, 15 ou 30 min pour la vraie, dans 100 % des tirages. Vitesses
// multiples de 3 sur cette carte : v × m ÷ 60 tombe juste à deux décimales.
const MINUTES_VF = [25, 50, 85, 110, 170];
const Q_MAL_LU = { 25: 1, 50: 2, 85: 5, 110: 6, 170: 10 };   // m minutes = lecture fautive de q quarts d'heure
const minutesTexte = (m) => (m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`);

const genFormuleVF = () => {
    for (let essai = 0; essai < 60; essai++) {
        const mob = randomChoice(MOBILES);
        const v = randomChoice(mob.vs.filter((x) => x % 3 === 0));
        const q1 = randomChoice(Q_LISIBLES.filter((x) => x <= mob.qMax));
        const m2 = randomChoice(MINUTES_VF.filter((x) => Q_MAL_LU[x] <= mob.qMax));
        const verites = [randInt(0, 1), randInt(0, 1), randInt(0, 1), randInt(0, 1)];
        if (verites.every((x) => x === verites[0])) continue;       // au moins une vraie et une fausse
        const d1 = ratMul(ratN(v), heures(q1));
        const d2 = verites[1] === 0 ? ratDiv(ratMul(ratN(v), ratN(m2)), ratN(60)) : ratMul(ratN(v), heures(Q_MAL_LU[m2]));
        if (minutesTexte(m2) !== dureeMalLue(Q_MAL_LU[m2])) throw new Error('gen_173 V/F : lecture fautive mal écrite');
        // second chemin : la durée affichée est la bonne si et seulement si la ligne est vraie
        if (ratEq(ratDiv(d2, ratN(v)), rat(m2, 60)) !== (verites[1] === 0)) throw new Error('gen_173 V/F : durée incohérente');
        if (nbDecimales(d2) > 2) continue;
        const dAffichee = verites[0] === 0 ? d1 : ratMul(ratN(v), heuresMalLues(q1));
        const lignes = [
            { t: `En ${dureeTexte(q1)}, ${mob.le} parcourt ${nb(dAffichee)} km.`, v: verites[0], type: 'distance' },
            { t: `Il faut ${minutesTexte(m2)} pour parcourir ${nb(d2)} km.`, v: verites[1], type: 'duree' },
            (() => { const f = randomChoice(FORMULES_VF); return { t: verites[2] === 0 ? f.vraie : f.fausse, v: verites[2], type: 'formule' }; })(),
            { t: randomChoice(verites[3] === 0 ? PROPRIETES_VF.vraies : PROPRIETES_VF.fausses), v: verites[3], type: 'propriete' },
        ];
        // second chemin : la ligne de distance fausse ne vaut jamais la bonne distance
        if (verites[0] === 1 && ratEq(dAffichee, d1)) continue;
        const melange = shuffle(lignes);
        const payload = makeVraiFaux(melange.map((l) => l.t), melange.map((l) => l.v),
            `<p>${mob.sujet} à la vitesse constante de ${v} km/h. On note ${tex('d')} la distance parcourue, en km, `
            + `${tex('t')} la durée du trajet, en h, et ${tex('v')} la vitesse, en km/h.</p>`
            + '<p><b>Pour chaque affirmation, dis si elle est vraie ou fausse.</b></p>',
            opts(L_FORMULE));
        payload._meta.params = { mobile: mob.id, v, vraies: lignes.filter((l) => l.v === 0).map((l) => l.type) };
        return payload;
    }
    throw new Error('gen_173 V/F : aucun tirage valide');
};

// ═══════════════════════════════════════════════════════════
// 6. RELIER — intensité ↔ tension (loi d'Ohm appliquée)
// ═══════════════════════════════════════════════════════════

const genFormuleRelier = () => {
    // Refonte bilan prof D3 (2026-07-27) : gabarit 2 sources / 4 cibles, R
    // affiché dans l'énoncé. Leurres TOUS en volts (aucune trahison par
    // l'unité), candidats par ordre de priorité, dédupliqués contre toutes les
    // cibles et entre eux :
    //   · additif : R + i (« il additionne au lieu de multiplier ») — un par source ;
    //   · recopie_unite : i tout court en volts (recopie l'intensité, zéro calcul) ;
    //   · division : i ÷ R quand c'est entier.
    // Revue 2026-09-23 : intensités de 1 à 4 A (tensions de 24 V au plus), lettres
    // et formule en KaTeX, « Deux tensions sont en trop. ».
    const R = randomChoice([2, 3, 4, 5, 6]);
    const Is = shuffle([1, 2, 3, 4]).slice(0, 2);
    const pairs = Is.map((i) => ({ source: `${i} A`, target: `${R * i} V` }));

    const valeursPrises = new Set(Is.map((i) => R * i));
    const candidats = [
        { v: R + Is[0], code: 'additif' },
        { v: R + Is[1], code: 'additif' },
        ...Is.map((i) => ({ v: i, code: 'recopie_unite' })),
        ...Is.filter((i) => i % R === 0).map((i) => ({ v: i / R, code: 'division' })),
    ];
    const leurres = [];
    for (const c of candidats) {
        if (leurres.length >= 2) break;
        if (valeursPrises.has(c.v)) continue;
        valeursPrises.add(c.v);
        leurres.push({ target: `${c.v} V`, code: c.code });
    }
    if (leurres.length !== 2) throw new Error('gen_173 relier : deux leurres attendus');

    const payload = makeRelier(pairs,
        `<p>On applique la loi d'Ohm ${tex('U = R \\times I')} avec une résistance ${tex(`R = ${R}`)} Ω.</p>`
        + `<p><b>Relie chaque intensité ${tex('I')} à la tension ${tex('U')} correspondante. Deux tensions sont en trop.</b></p>`,
        { ...opts(L_FORMULE), cibles_leurres: leurres });
    payload._meta.params = {
        R, Is,
        distracteurs: Object.fromEntries(leurres.map((l) => [l.target, l.code])),
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    formule_texte: genFormuleSaisie,
    cercle_texte: genCercleSaisie,
    formule_qcm_unique: genFormuleQcm,
    tableau_texte_trou: genTableauTrou,
    formule_tableau_vf: genFormuleVF,
    formule_relier: genFormuleRelier,
};

export const CATEGORIES = [
    {
        id: 'formule',
        label: L_FORMULE,
        formatsList: [
            { key: 'formule_texte', label: 'Appliquer une formule' },
            { key: 'cercle_texte', label: 'Longueur d\'un cercle' },
            { key: 'formule_qcm_unique', label: 'Choisir le calcul (QCM)' },
            { key: 'tableau_texte_trou', label: 'Tableau des distances' },
            { key: 'formule_tableau_vf', label: 'Vrai/Faux' },
            { key: 'formule_relier', label: 'Relier' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
